import { IUserDetails } from "../@types/Login";
import { AUTH_USER_DETAILS } from "../constants/localStorageKeys";
import { safeJsonParse, safeStorage } from "./storage";

/**
 * Role codes shipped by the API in the login response (EN-4961).
 * Mirrors the C# `pMp.Domain.Constants.RoleCodes` enum.
 */
export const ROLES = {
  FIRST_LINE_HCP: "first-line-hcp",
  SECOND_LINE_HCP: "second-line-hcp",
  HEALTHCARE_ADMIN: "healthcare-admin",
  SYSTEM_CONTROLLER: "system-controller",
  SUPER_ADMIN: "super-admin",
  LEGACY: "legacy",
  LEGACY_ADMIN: "legacy-admin",
} as const;

export type IRoleCode = (typeof ROLES)[keyof typeof ROLES];

/**
 * Returns the authenticated user's details envelope from localStorage, or
 * null if it's missing/malformed. Shared by the role/permission helpers and
 * any UI that needs raw fields (email, first name) off the same payload.
 */
export function getUserDetails(): IUserDetails | null {
  return safeJsonParse<IUserDetails | null>(
    safeStorage.getItem(AUTH_USER_DETAILS),
    null
  );
}

/**
 * Returns the authenticated user's role code, or null if the user has no
 * role (e.g. a legacy session predating EN-4961, or unauthenticated state).
 * Reads from the AUTH_USER_DETAILS localStorage key — role lives on the
 * `userDetails` envelope alongside email/name.
 */
export function getUserRole(): string | null {
  return getUserDetails()?.role ?? null;
}

/**
 * Returns true iff the authenticated user holds the SystemController role.
 * Used for routing decisions (e.g. redirecting to the admin reports
 * dashboard on login). Identity-based — distinct from the permission
 * check (`isAdminUser()` / `admin-reports:read`) which Phase 2 may
 * grant to additional roles.
 */
export function isSystemControllerUser(): boolean {
  return getUserRole() === ROLES.SYSTEM_CONTROLLER;
}
